import "./marker-edit-panel.scss";

import type { ReactNode } from "react";
import { useCallback } from "react";
import { connect } from "react-redux";

import actions from "@/actions";
import { ConcealablePanel } from "@/components";
import { markerTypeLabels } from "@/constants/marker-constants";
import type { MarkerPosition } from "@/models/marker-position";

type MarkerField = "identifier" | "notes";

interface MarkerEditPanelProps {
    /**
     * The currently selected marker, or null if no single marker is selected.
     */
    marker: MarkerPosition | null;
    /**
     * Callback to update marker positions in the store.
     */
    updateMarkers: (markers: Record<string, MarkerPosition>) => void;
}

const MarkerEditPanelComponent = ({
    marker,
    updateMarkers,
}: MarkerEditPanelProps): ReactNode => {
    const onChange = useCallback(
        (field: MarkerField, value: string) => {
            if (!marker) return;

            updateMarkers({
                [marker.uniqueId]: {
                    ...marker,
                    [field]: value,
                },
            });
        },
        [marker, updateMarkers],
    );

    if (!marker) return null;

    return (
        <ConcealablePanel className="marker-edit-panel" title="Edit">
            <div className="marker-edit-bar">
                {markerTypeLabels[marker.markerType] ?? marker.markerType}
            </div>
            <div className="marker-edit-form">
                <div className="marker-edit-field">
                    <label>Identifier</label>
                    <input
                        type="text"
                        value={marker.identifier ?? ""}
                        onChange={(e) => onChange("identifier", e.target.value)}
                    />
                </div>
                <div className="marker-edit-field">
                    <label>Notes</label>
                    <textarea
                        value={marker.notes ?? ""}
                        onChange={(e) => onChange("notes", e.target.value)}
                    />
                </div>
            </div>
        </ConcealablePanel>
    );
};

const mapStateToProps = ({ map }: { map: any }) => {
    const { selectedMarkers, mode } = map.general;
    const { markers } = map.values.global.present;

    const marker =
        mode.name !== "add" && selectedMarkers.length === 1
            ? markers[selectedMarkers[0]]
            : null;

    return { marker };
};

const mapDispatchToProps = {
    updateMarkers: actions.map.values.global.updateMarkers,
};

export const MarkerEditPanel = connect(
    mapStateToProps,
    mapDispatchToProps,
)(MarkerEditPanelComponent);
